import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { TeamProvider } from './lib/teamConfig';
import { AuthProvider } from './lib/auth';
import { useOpenReplay } from './hooks/useOpenReplay';
import ErrorBoundary from './components/design-system/ErrorBoundary';
import { TeamThemeProvider } from './components/design-system';
import MLBLayout from './components/mlb/MLBLayout';
import GmLayout from './components/gm/GmLayout';
import PasswordGate from './components/auth/PasswordGate';
import { MlbTeamProvider, useMlbTeam } from './lib/mlb/MlbTeamContext';
import { MlbTeamDataProvider } from './lib/mlb/MlbTeamDataContext';
import { getClientDisplayName, getClientProgramLabel } from './utils/clientBranding';

// MLB routes
const PitchTippingPage = lazy(() => import('./pages/mlb/PitchTippingPage'));
const OverviewPage = lazy(() => import('./pages/mlb/OverviewPage'));
const RosterPage = lazy(() => import('./pages/mlb/RosterPage'));
const AskAIPage = lazy(() => import('./pages/mlb/AskAIPage'));
const DiamondEyePage = lazy(() => import('./pages/mlb/DiamondEyePage'));

// GM routes
const GmDashboardPage = lazy(() => import('./pages/gm/GmDashboardPage'));
const GmRosterPage = lazy(() => import('./pages/gm/GmRosterPage'));
const GmStubPage = lazy(() => import('./pages/gm/GmStubPage'));
const GmTradeBlockPage = lazy(() => import('./pages/gm/GmTradeBlockPage'));
const GmTradeSimulatePage = lazy(() => import('./pages/gm/GmTradeSimulatePage'));
const GmFilmRoomPage = lazy(() => import('./pages/gm/GmFilmRoomPage'));
const GmUsagePage = lazy(() => import('./pages/gm/GmUsagePage'));
const GmEthosPage = lazy(() => import('./pages/gm/GmEthosPage'));
const GmTasksPage = lazy(() => import('./pages/gm/GmTasksPage'));
const GmAdminPage = lazy(() => import('./pages/gm/GmAdminPage'));

const Loading = () => (
  (() => {
    const clientName = getClientDisplayName();
    const clientProgramLabel = getClientProgramLabel();
    const clientBrandLine = clientProgramLabel && clientProgramLabel !== clientName
      ? `${clientName} • ${clientProgramLabel}`
      : clientName;
    return (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#10141a',
    color: '#dfe2eb',
    fontFamily: 'Inter, sans-serif',
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '2rem',
        fontWeight: 700,
        marginBottom: '0.5rem',
        color: '#FED603',
      }}>
        SLAM
      </div>
      <div style={{ fontSize: '0.875rem', opacity: 0.6, fontFamily: 'Manrope, sans-serif' }}>
        {clientBrandLine}
      </div>
    </div>
  </div>
    );
  })()
);

// Initializes OpenReplay session recording inside AuthProvider context
function OpenReplayInit({ children }) {
  useOpenReplay();
  return children;
}

// Bridges the live team selection (MlbTeamProvider) into the CSS theme layer
// (TeamThemeProvider) so [data-team] follows the picker instead of being pinned.
function ThemeBridge({ children }) {
  const { team } = useMlbTeam();
  return <TeamThemeProvider team={team}>{children}</TeamThemeProvider>;
}

// Team Tasks is a toggleable demo surface (FB-2026-05-15-01). Default on;
// set REACT_APP_FEATURE_TEAM_TASKS=false to hide the route.
const TEAM_TASKS_ENABLED = process.env.REACT_APP_FEATURE_TEAM_TASKS !== 'false';

function App() {
  useEffect(() => {
    const name = getClientDisplayName();
    // Match static index.html ("SLAM MLB") when the default fallback is in
    // effect so the tab doesn't flicker between "SLAM MLB" → "SLAM — MLB".
    document.title = name === 'MLB' ? 'SLAM MLB' : `SLAM — ${name}`;
    const desc = `SLAM Sports AI — MLB Team Dashboard. AI-powered scouting, advance scouting, Diamond Eye video analysis, and roster intelligence.`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', desc);
  }, []);

  return (
    <ErrorBoundary>
      <MlbTeamProvider>
      <ThemeBridge>
      <TeamProvider>
        <BrowserRouter>
          <AuthProvider>
            <OpenReplayInit>
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Demo password gate — replaces /login flow from slam-creighton seed */}
                <Route element={<PasswordGate />}>
                  {/* Root → GM dashboard (unified shell landing) */}
                  <Route path="/" element={<Navigate to="/gm/dashboard" replace />} />

                  {/* Legacy coaching routes → redirect into unified GM shell */}
                  <Route path="/coach/*" element={<Navigate to="/gm/dashboard" replace />} />

                  {/* Unified GM shell — both /gm/* and /mlb/* routes share the
                      GmLayout (sidebar + header). MlbTeamProvider scopes the
                      global team picker to this shell. MLB pages slot into
                      the new "MLB SUITE" sidebar section. */}
                  <Route element={(
                    <MlbTeamDataProvider>
                      <GmLayout />
                    </MlbTeamDataProvider>
                  )}>
                    <Route path="/gm" element={<Navigate to="/gm/dashboard" replace />} />
                    <Route path="/gm/dashboard" element={<GmDashboardPage />} />
                    <Route path="/gm/roster" element={<GmRosterPage />} />
                    <Route path="/gm/tasks" element={TEAM_TASKS_ENABLED ? <GmTasksPage /> : <Navigate to="/gm/dashboard" replace />} />
                    {/* Advance scouting routes are unified — both URLs render the
                        full-corpus PitchTippingPage (the 1,700-video multi-pitcher
                        tipping dashboard with Other Tells, video carousels, lens
                        grades). GmAdvancedScoutingPage (Wake Forest synced-grid)
                        stays on disk as a single-pitcher sub-view for ep10 when
                        we wire a data-source picker. */}
                    <Route path="/gm/advanced-scouting" element={<Navigate to="/mlb/advance-scouting" replace />} />
                    <Route path="/gm/advance-scouting" element={<Navigate to="/mlb/advance-scouting" replace />} />
                    <Route path="/gm/trade-block" element={<GmTradeBlockPage />} />
                    <Route path="/gm/trade-block/cba-simulate" element={<GmTradeSimulatePage />} />
                    <Route path="/gm/film-room" element={<GmFilmRoomPage />} />
                    <Route path="/gm/admin" element={<GmAdminPage />} />
                    <Route path="/gm/usage" element={<GmUsagePage />} />
                    <Route path="/gm/ethos" element={<GmEthosPage />} />
                    {/* ep9 W6 — "Replicate Iona/Creighton dashboard on the major-league
                        side" (Apr 27 sync with Rob). GmDashboardPage is already team-aware
                        via useMlbTeamData(), so /mlb/dashboard mounts the same component
                        without a fork-and-port. /mlb/overview stays available for the
                        leaderboard-centric view. */}
                    <Route path="/mlb" element={<Navigate to="/mlb/dashboard" replace />} />
                    <Route path="/mlb/dashboard" element={<GmDashboardPage />} />
                    <Route path="/mlb/overview" element={<OverviewPage />} />
                    <Route path="/mlb/roster" element={<RosterPage />} />
                    {/* Unified Advance Scouting (ep9, 2026-05-13 revised).
                        PitchTippingPage is the canonical, full-corpus interface —
                        1,700-video tipping dashboard with Other Tells, lens grades,
                        and the per-pitcher intel loader. /gm/advanced-scouting
                        and /mlb/pitch-tipping both redirect here.
                        GmAdvancedScoutingPage (Wake Forest synced-grid) is a
                        single-pitcher sub-view kept available at
                        /mlb/advance-scouting/wake-forest until a tab system
                        lands in ep10. */}
                    <Route path="/mlb/advance-scouting" element={<PitchTippingPage />} />
                    <Route path="/mlb/pitch-tipping" element={<Navigate to="/mlb/advance-scouting" replace />} />
                    <Route path="/mlb/advance-scouting/wake-forest" element={<Navigate to="/mlb/advance-scouting?dataset=college" replace />} />
                    <Route path="/mlb/diamond-eye" element={<DiamondEyePage />} />
                    <Route path="/mlb/ask-ai" element={<AskAIPage />} />
                  </Route>
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/gm/dashboard" replace />} />
              </Routes>
            </Suspense>
            </OpenReplayInit>
          </AuthProvider>
        </BrowserRouter>
      </TeamProvider>
      </ThemeBridge>
      </MlbTeamProvider>
    </ErrorBoundary>
  );
}

export default App;
